/**
 * Right after the user grants Usage Access (Android AppOps toggle), the OS can
 * report `granted` while still returning zero usage events for a short while
 * (OEM propagation lag — observed on Honor/MagicOS). Instead of settling on
 * the "no usage recorded" empty state, the first collect after a grant runs
 * immediately (debounce bypassed) and retries on a short backoff.
 */
export const POST_GRANT_RETRY_DELAYS_MS = [5_000, 15_000, 30_000] as const;

export type PermissionStatusLike = string | null | undefined;

/** True when live permission moved from a known non-granted state to granted. */
export function isPermissionGrantTransition(
  previous: PermissionStatusLike,
  next: PermissionStatusLike,
): boolean {
  return next === "granted" && previous != null && previous !== "granted";
}

export type PostGrantCollectResultLike = {
  permission?: string;
  totalSeconds?: number;
} | null | undefined;

/**
 * Delay before the next automatic post-grant retry, or null to stop retrying
 * (data arrived, permission regressed, or attempts are exhausted).
 */
export function nextPostGrantRetryDelayMs(
  result: PostGrantCollectResultLike,
  attempt: number,
): number | null {
  if (!result || result.permission !== "granted") return null;
  if ((result.totalSeconds ?? 0) > 0) return null;
  return POST_GRANT_RETRY_DELAYS_MS[attempt] ?? null;
}
